import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { Link } from 'react-router-dom';
import { loginRequest, getUserEmail } from './auth/authConfig';
import { apiCall, AuthError } from '../utils/apiUtils';
import { useAuth } from './authProvider/AuthContext';
import './Sidebar.css';

export default function Sidebar({
    onBlogSelect,
    onSidebarRefresh,
    expanded = false,
    onToggle,
    onClose,
    compact = false,
}) {
    const { instance, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const { authError, handleAuthError, clearAuthError, triggerReauth, user } = useAuth();
    const [error, setError] = useState(null);
    const [userName, setUserName] = useState("");
    const isExpanded = expanded;
    const [recentBlogs, setRecentBlogs] = useState([]);
    const [starredBlogs, setStarredBlogs] = useState([]);
    const [loadingBlogs, setLoadingBlogs] = useState(false);
    const [hasMoreRecent, setHasMoreRecent] = useState(false);
    const [hasMoreStarred, setHasMoreStarred] = useState(false);

    // Stable identity used as a useEffect dependency. `accounts` from
    // useMsal() is a new array reference on every render, which would
    // cause this component to refetch on every render and (when the
    // backend returns 401) spiral into thousands of retries per minute.
    const activeAccountId = accounts[0]?.homeAccountId ?? null;

    // Once a hard auth failure has been recorded, stop hammering the API.
    // Otherwise a single 401 keeps re-firing with every render.
    const reauthBlocked = !!authError?.requiresReauth;

    // Use ref to store latest loadBlogs function
    const loadBlogsRef = useRef();

    const loadBlogs = useCallback(async () => {
        if (!isAuthenticated || reauthBlocked) return;
        
        setLoadingBlogs(true);
        setError(null); // Clear any previous errors
        
        try {
            // Load recent blogs (max 5)
            const recentResponse = await apiCall('/get-user-blogs', null, {
                method: 'GET',
                params: {
                    page: 1,
                    page_size: 5,
                    starred_only: false,
                    sort_by: 'createdAt',
                    sort_order: 'desc'
                }
            });

            // Load starred blogs (max 5)
            const starredResponse = await apiCall('/get-user-blogs', null, {
                method: 'GET',
                params: {
                    page: 1,
                    page_size: 5,
                    starred_only: true,
                    sort_by: 'createdAt',
                    sort_order: 'desc'
                }
            });

            if (recentResponse && recentResponse.blogs) {
                setRecentBlogs(recentResponse.blogs);
                setHasMoreRecent(recentResponse.pagination && recentResponse.pagination.total_count > 5);
            }
            
            if (starredResponse && starredResponse.blogs) {
                setStarredBlogs(starredResponse.blogs);
                setHasMoreStarred(starredResponse.pagination && starredResponse.pagination.total_count > 5);
            }
        } catch (error) {
            console.error('Error loading blogs:', error);
            
            // Handle different types of errors
            if (error.type === AuthError.TOKEN_EXPIRED || error.type === AuthError.NO_ACCOUNT) {
                handleAuthError(error);
                setError('Your session has expired. Please sign in again to view your blogs.');
            } else if (error.type === AuthError.NETWORK_ERROR) {
                handleAuthError(error);
                setError('Network error. Please check your connection and try again.');
            } else if (error.type === AuthError.REFRESH_FAILED) {
                handleAuthError(error);
                setError('Authentication error. Please sign in again.');
            } else {
                setError('Failed to load blogs. Please try again.');
            }
        } finally {
            setLoadingBlogs(false);
        }
    }, [isAuthenticated, reauthBlocked, handleAuthError]);

    // Keep ref updated with latest loadBlogs function
    useEffect(() => {
        loadBlogsRef.current = loadBlogs;
    }, [loadBlogs]);

    useEffect(() => {
        const fetchDisplayName = async () => {
            const account = accounts[0];
            if (!account) return;
            const userEmail = getUserEmail(account);
            try {
                // Fetch user profile from Cosmos DB to get display name
                const response = await apiCall('/get-profile-by-email', null, {
                    method: 'GET',
                    params: { email: userEmail }
                });

                if (response && response.displayName) {
                    setUserName(response.displayName);
                } else {
                    // Fallback to account name if no display name in profile
                    setUserName(account.name || userEmail || "User");
                }
            } catch (err) {
                console.error('Error fetching display name:', err);

                // Handle authentication errors
                if (err.type === AuthError.TOKEN_EXPIRED || err.type === AuthError.NO_ACCOUNT) {
                    handleAuthError(err);
                }

                // Use account name as fallback regardless of error type
                setUserName(account.name || userEmail || "User");
            }
        };

        if (isAuthenticated && !reauthBlocked) {
            fetchDisplayName();
            loadBlogs();
        }
        // Depend on the stable account id (not the array reference) so this
        // effect only re-runs when the user actually changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAccountId, isAuthenticated, reauthBlocked, loadBlogs, handleAuthError]);

    // Expose refresh function to parent
    useEffect(() => {
        if (onSidebarRefresh) {
            onSidebarRefresh(() => loadBlogsRef.current?.());
        }
    }, [onSidebarRefresh]);

    const handleBlogClick = async (blog) => {
        try {
            // Use the onBlogSelect callback to navigate and load the blog
            if (onBlogSelect) {
                onBlogSelect(blog);
            }
            handleNavigate();
        } catch (error) {
            console.error('Error loading blog:', error);
        }
    };

    const handleStarToggle = async (blog, event) => {
        event.stopPropagation(); // Prevent blog click
        
        try {
            const response = await apiCall(`/toggle-blog-star?id=${blog.id}`, null, {
                method: 'POST'
            });

            if (response) {
                // Reload both lists to reflect the change
                loadBlogs();
            }
        } catch (error) {
            console.error('Error toggling star:', error);
            
            // Handle authentication errors
            if (error.type === AuthError.TOKEN_EXPIRED || error.type === AuthError.NO_ACCOUNT) {
                handleAuthError(error);
                setError('Your session has expired. Please sign in again.');
            } else if (error.type === AuthError.NETWORK_ERROR) {
                setError('Network error. Please check your connection and try again.');
            } else {
                setError('Failed to update blog. Please try again.');
            }
        }
    };

    const truncateTitle = (title, maxLength = 30) => {
        return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
    };

    const handleLogin = async () => {
        try {
            setError(null);
            clearAuthError();
            await instance.loginRedirect(loginRequest);
        } catch (err) {
            console.error("Login error:", err);
            setError("Failed to sign in. Please try again.");
        }
    };

    const handleLogout = async () => {
        try {
            setError(null);
            clearAuthError();
            // Pass the raw id_token as idTokenHint so CIAM can skip its
            // account-picker and redirect straight back to the app. Without
            // the hint, the /logout endpoint stalls on "Pick an account",
            // which looks to the user like the app has frozen.
            const account = accounts[0] ?? instance.getActiveAccount();
            await instance.logoutRedirect({
                account,
                idTokenHint: account?.idToken,
            });
        } catch (err) {
            console.error("Logout error:", err);
            setError("Failed to sign out. Please try again.");
        }
    };

    const toggleSidebar = () => {
        if (onToggle) onToggle();
    };

    const handleBackdropClick = () => {
        if (onClose) onClose();
    };

    const handleNavigate = () => {
        // When a nav item is tapped in compact mode, close the drawer so the
        // user sees the destination rather than an overlaid menu.
        if (compact && onClose) onClose();
    };

    // Handle authentication errors from the auth context
    const displayError = error || (authError?.requiresReauth ? authError.message : null);

    const sidebarClassName = [
        'sidebar',
        isExpanded ? 'expanded' : 'collapsed',
        compact ? 'sidebar--compact' : '',
    ].filter(Boolean).join(' ');

    return (
        <>
            {compact && isExpanded && (
                <div
                    className="sidebar-backdrop"
                    onClick={handleBackdropClick}
                    aria-hidden="true"
                />
            )}
            <aside
                id="app-sidebar"
                className={sidebarClassName}
                aria-hidden={compact && !isExpanded ? 'true' : undefined}
            >
                <div
                    className="sidebar-toggle"
                    onClick={toggleSidebar}
                    role="button"
                    tabIndex={0}
                    aria-label={isExpanded ? 'Close menu' : 'Open menu'}
                    aria-expanded={isExpanded}
                    aria-controls="app-sidebar"
                >
                    <div className="hamburger-icon">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>

                <div className="sidebar-content">
                {displayError && (
                    <div className="sidebar-error">
                        {displayError}
                        {authError?.requiresReauth && (
                            <button 
                                className="retry-auth-button"
                                onClick={triggerReauth}
                                style={{
                                    marginLeft: '8px',
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    backgroundColor: '#007acc',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Sign In
                            </button>
                        )}
                    </div>
                )}
                
                {isAuthenticated ? (
                    <div className="sidebar-user-section">
                        <div className="sidebar-user-info">
                            <div className="user-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                    <circle cx="12" cy="7" r="4"/>
                                </svg>
                            </div>
                            {isExpanded && <span className="sidebar-user-name">{userName}</span>}
                        </div>

                        {isExpanded && (
                            <div className="sidebar-blogs-section">
                                {/* Starred Section */}
                                <div className="blog-section">
                                    <div className="section-header">starred</div>
                                    <div className="blogs-list">
                                        {loadingBlogs && starredBlogs.length === 0 ? (
                                            <div className="loading-indicator">Loading...</div>
                                        ) : starredBlogs.length > 0 ? (
                                            <>
                                                {starredBlogs.map((blog) => (
                                                    <div 
                                                        key={blog.id} 
                                                        className="blog-item"
                                                        onClick={() => handleBlogClick(blog)}
                                                        title={blog.h1Title}
                                                    >
                                                        <span className="blog-title">
                                                            {truncateTitle(blog.h1Title)}
                                                        </span>
                                                        <button
                                                            className="star-button starred"
                                                            onClick={(e) => handleStarToggle(blog, e)}
                                                            title="Remove from starred"
                                                        >
                                                            ⭐
                                                        </button>
                                                    </div>
                                                ))}
                                                {hasMoreStarred && (
                                                    <Link to="/profile?tab=blogs&filter=starred" className="see-more-link">
                                                        See more starred blogs
                                                    </Link>
                                                )}
                                            </>
                                        ) : (
                                            <div className="no-blogs">No starred blogs yet</div>
                                        )}
                                    </div>
                                </div>

                                {/* Recent Section */}
                                <div className="blog-section">
                                    <div className="section-header">recents</div>
                                    <div className="blogs-list">
                                        {loadingBlogs && recentBlogs.length === 0 ? (
                                            <div className="loading-indicator">Loading...</div>
                                        ) : recentBlogs.length > 0 ? (
                                            <>
                                                {recentBlogs.map((blog) => (
                                                    <div 
                                                        key={blog.id} 
                                                        className="blog-item"
                                                        onClick={() => handleBlogClick(blog)}
                                                        title={blog.h1Title}
                                                    >
                                                        <span className="blog-title">
                                                            {truncateTitle(blog.h1Title)}
                                                        </span>
                                                        <button
                                                            className={`star-button ${blog.isStarred ? 'starred' : ''}`}
                                                            onClick={(e) => handleStarToggle(blog, e)}
                                                            title={blog.isStarred ? 'Remove from starred' : 'Add to starred'}
                                                        >
                                                            ⭐
                                                        </button>
                                                    </div>
                                                ))}
                                                {hasMoreRecent && (
                                                    <Link to="/profile?tab=blogs" className="see-more-link">
                                                        See more blogs
                                                    </Link>
                                                )}
                                            </>
                                        ) : (
                                            <div className="no-blogs">No blogs yet</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div className="sidebar-bottom-section">
                            <Link to="/profile" className="sidebar-menu-item" title="Settings" onClick={handleNavigate}>
                                <div className="menu-icon">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="3"/>
                                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                                    </svg>
                                </div>
                                {isExpanded && <span>Settings</span>}
                            </Link>

                            {user?.isAdmin && (
                                <Link to="/admin" className="sidebar-menu-item" title="Admin" onClick={handleNavigate}>
                                    <div className="menu-icon">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                        </svg>
                                    </div>
                                    {isExpanded && <span>Admin</span>}
                                </Link>
                            )}
                            
                            <button className="sidebar-menu-item" onClick={handleLogout} title="Sign Out">
                                <div className="menu-icon">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                                        <polyline points="16,17 21,12 16,7"/>
                                        <line x1="21" y1="12" x2="9" y2="12"/>
                                    </svg>
                                </div>
                                {isExpanded && <span>Sign Out</span>}
                            </button>
                        </div>
                    </div>
                ) : (
                    <button className="sidebar-menu-item" onClick={handleLogin} title="Sign In">
                        <div className="menu-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                                <polyline points="10,17 15,12 10,7"/>
                                <line x1="15" y1="12" x2="3" y2="12"/>
                            </svg>
                        </div>
                        {isExpanded && <span>Sign In</span>}
                    </button>
                )}
                </div>
            </aside>
        </>
    );
} 